import {
  RawActiveDistribution,
  RawOrderDetailsResponse,
  RawStorePickup,
  RawShippingAndHandling,
  RawBodfsDeliveryAndHandling
} from '../types';
import { OrderDetailsQueryParams } from '../orderDetailsDataModel';

export interface BuildActiveOrderDetailsParamsIdentity {
  userId: string;
  customerAccountId: string;
  tmxProfileId?: string;
  channelId?: string;
}

// Loose union of both possible lineItems shapes (activeOrders' RawActiveOrderLineItem, or
// orderDetails' richer RawFulfillmentGroupLineItem) — FulfillmentDetailsDrawer's component tree
// treats `fulfillmentGroup` as untyped (`any`) throughout anyway (see its own props interfaces), so
// this stays deliberately permissive rather than forcing every consumer to discriminate which
// source a given item came from.
export interface FulfillmentGroupLineItem {
  name?: string | null;
  thdSku?: string | null;
  lineId?: string | null;
  omsId?: number | null;
  skuNumber?: number | null;
  quantity?: number | null;
  currentQuantity?: number | null;
  orginalOrderedQuantity?: number | null;
  qtyToPrint?: number | null;
  unitPrice?: number | null;
  totalPrice?: number | null;
  imageUrl?: string | null;
  statusCode?: number | null;
  statusDescription?: string | null;
  brandName?: string | null;
  description?: string | null;
  subClassName?: string | null;
  pickedUpDate?: string | null;
  pickupDate?: string | null;
  deliveredDateArray?: string[] | null;
  actions?: {
    buyAgain?: boolean | null;
    infoAndGuides?: boolean | null;
    returnDropoffAllowed?: boolean | null;
    returnLORPAllowed?: boolean | null;
    returnInStoreAllowed?: boolean | null;
    writeAReview?: boolean | null;
    trackingEnabled?: boolean | null;
  } | null;
}

export interface FulfillmentGroupViewModel {
  orderNumber?: string;
  projectName?: string;
  poJobName?: string;
  orderOrigin?: string;
  salesDate?: string;
  transactionId?: string;
  registerNumber?: string;
  storeNumber?: string;
  fulfillmentStore?: string;
  fulfillmentType?: string;
  fulfillmentDate?: string;
  storePickup?: RawStorePickup;
  shippingAndHandling?: RawShippingAndHandling;
  bodfsDeliveryAndHandling?: RawBodfsDeliveryAndHandling;
  lineItems?: FulfillmentGroupLineItem[];
}

/**
 * Determines which orderDetails query variant (online vs in-store) an active order needs,
 * mirroring purchaseHistoryToRow.ts's buildOrderDetailsParams heuristic 1:1 — same
 * orderOrigin-then-orderNumber-prefix fallback for "online", same four-required-fields check for
 * "inStore" — but reading off RawActiveDistribution's OOP-4197-added identifier fields instead of
 * RawPurchaseHistoryOrder's. Returns null when the distribution doesn't carry enough identifying
 * data for either variant — the caller skips the query for that row rather than send an incomplete
 * request, and the drawer renders from the raw distribution alone.
 *
 * PRODUCTION NOTE: `orderOrigin`/`storeNumber`/`transactionId`/`registerNumber`/`salesDate` were
 * all confirmed REJECTED by the real stage schema and removed from activeOrdersDataModel.ts's
 * selection (see that file's and types.ts's header comments) — a real `distribution` will never
 * have these set. In production this always falls through to the orderNumber-prefix regex for
 * "online" (never the orderOrigin check), and the "inStore" branch is effectively dead code until
 * a schema exposing equivalent fields is targeted. Kept rather than deleted, for the same reason
 * activeOrdersDataModel.ts keeps its own rejected fields declared: only that one file needs to
 * change once a real field name is confirmed.
 */
export function buildActiveOrderDetailsParams(
  distribution: RawActiveDistribution,
  { userId, customerAccountId, tmxProfileId, channelId }: BuildActiveOrderDetailsParamsIdentity
): OrderDetailsQueryParams | null {
  const isOnlineOrder = distribution.orderOrigin
    ? distribution.orderOrigin.toLowerCase() === 'online'
    : /^[wc]/i.test(distribution.orderNumber ?? '');

  if (isOnlineOrder) {
    if (!distribution.orderNumber) {
      return null;
    }
    return { variant: 'online', userId, customerAccountId, orderNumber: distribution.orderNumber, tmxProfileId, channelId };
  }

  if (
    distribution.storeNumber != null
    && distribution.transactionId != null
    && distribution.registerNumber != null
    && distribution.salesDate
  ) {
    return {
      variant: 'inStore',
      userId,
      customerAccountId,
      storeNumber: distribution.storeNumber,
      transactionId: distribution.transactionId,
      registerNumber: distribution.registerNumber,
      salesDate: distribution.salesDate,
      tmxProfileId,
      channelId
    };
  }

  return null;
}

/**
 * Merges a raw activeOrders distribution with its (optional) orderDetails result into the flat
 * object src/components/common/FulfillmentDrawer's component tree expects as its
 * `fulfillmentGroup` prop. This is the graceful-degrade mechanism the ticket's AC asks for:
 * `orderDetails` is only passed when the query actually succeeded (see ActiveOrdersTable.tsx) —
 * loading/error/idle/skipped all mean it's `undefined` here, so every orderDetails-only field below
 * falls back to `undefined` rather than a synthesized guess, and the drawer's defensive `?.` chains
 * render the same empty/default state they did before this integration existed. The raw
 * distribution's own fields (orderNumber/projectName/poJobName/lineItems) are never blocked by a
 * failed orderDetails query.
 */
export function mergeDistributionWithOrderDetails(
  distribution: RawActiveDistribution,
  orderDetails?: RawOrderDetailsResponse
): FulfillmentGroupViewModel {
  const fulfillmentGroup = orderDetails?.fulfillmentGroups?.[0];

  return {
    orderNumber: distribution.orderNumber ?? undefined,
    projectName: distribution.projectName ?? undefined,
    poJobName: distribution.poJobName ?? undefined,
    orderOrigin: distribution.orderOrigin ?? undefined,
    salesDate: distribution.salesDate ?? undefined,
    transactionId: distribution.transactionId != null ? String(distribution.transactionId) : undefined,
    registerNumber: distribution.registerNumber != null ? String(distribution.registerNumber) : undefined,
    storeNumber: distribution.storeNumber != null ? String(distribution.storeNumber) : undefined,
    fulfillmentStore: distribution.location?.storeId ?? undefined,
    // Everything below only exists once orderDetails resolves successfully.
    // NOTE: no `deliveryTrackerData` here — that comes from a separate REST call
    // (useDeliveryTrackerQuery.ts), spliced into this object by ActiveOrdersTable.tsx itself, not
    // from orderDetails. See this file's module comment and types.ts's RawFulfillmentGroup header
    // comment for why.
    fulfillmentType: fulfillmentGroup?.fulfillmentType ?? undefined,
    fulfillmentDate: fulfillmentGroup?.fulfillmentDate ?? undefined,
    storePickup: fulfillmentGroup?.storePickup ?? undefined,
    shippingAndHandling: fulfillmentGroup?.shippingAndHandling ?? undefined,
    bodfsDeliveryAndHandling: fulfillmentGroup?.bodfsDeliveryAndHandling ?? undefined,
    // Prefer orderDetails' richer per-line-item status/brand/description fields; fall back to the
    // raw distribution's (statusCode/statusDescription-less) lineItems so the drawer still shows
    // item thumbnails/quantities when orderDetails hasn't resolved.
    lineItems: fulfillmentGroup?.lineItems ?? distribution.lineItems ?? undefined
  };
}
